import { DownloadOutlined, PhoneOutlined } from '@ant-design/icons';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Segmented, Tag, Typography, message } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@nks/shared';
import { ListPage } from '../../components/ListPage';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/format';
import { useHasPermission } from '../auth/session';
import { downloadCsv } from './csv';
import {
  exportFollowUpsCsv,
  listFollowUps,
  type FollowUp,
  type FollowUpView,
} from './api';
import { FollowUpOutcomeDrawer } from './FollowUpOutcomeDrawer';
import { SendReminderDrawer, type SendableReminder } from './ReminderDrawers';

const { Text } = Typography;

const bike = (f: FollowUp): string => f.unit?.plateNo ?? f.unit?.chassisNo ?? '—';

function PhoneLink({ phone }: { phone: string }): ReactNode {
  return (
    <a
      href={`tel:${phone}`}
      onClick={(e) => { e.stopPropagation(); }}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 32 }}
    >
      <PhoneOutlined />
      {phone}
    </a>
  );
}

/** The calls still to make: mine first, what is due today and what is overdue one tap away. */
export function FollowUpsPage(): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [view, setView] = useState<FollowUpView>('mine');
  const [selected, setSelected] = useState<FollowUp | null>(null);
  const [sending, setSending] = useState<SendableReminder | null>(null);
  const canSend = useHasPermission(PERMISSIONS.REMINDER_SEND);

  const { data, isFetching, error, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['follow-ups', view],
    queryFn: ({ pageParam }) => listFollowUps({ view, ...(pageParam !== undefined ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore && last.nextCursor !== null ? last.nextCursor : undefined),
  });
  // The API lists open calls by due time, so overdue ones come first.
  const rows = data?.pages.flatMap((p) => p.items) ?? [];

  const emptyFor: Record<FollowUpView, string> = {
    mine: t('serviceDesk.followUps.emptyMine'),
    today: t('serviceDesk.followUps.emptyToday'),
    overdue: t('serviceDesk.followUps.emptyOverdue'),
    everyone: t('serviceDesk.followUps.emptyEveryone'),
  };

  const overdueTag = (row: FollowUp): ReactNode =>
    row.overdue ? <Tag color="error">{t('serviceDesk.overdue')}</Tag> : null;

  // A reminder call is usually done by sending the reminder, which also ticks the call off.
  const reminderOf = (row: FollowUp): SendableReminder | null =>
    canSend && row.type === 'SERVICE_REMINDER' && row.source?.type === 'REMINDER' && row.unit !== null
      ? { uid: row.source.uid, customer: row.customer, unit: row.unit }
      : null;

  const record = (row: FollowUp): ReactNode => {
    const reminder = reminderOf(row);
    return (
      <Flex gap={8} wrap>
        {reminder !== null && (
          <Button
            type="primary"
            size="large"
            onClick={(e) => {
              e.stopPropagation();
              setSending(reminder);
            }}
          >
            {t('serviceDesk.reminders.send')}
          </Button>
        )}
        <Button
          type={reminder !== null ? 'default' : 'primary'}
          size="large"
          onClick={(e) => {
            e.stopPropagation();
            setSelected(row);
          }}
        >
          {t('serviceDesk.followUps.recordOutcome')}
        </Button>
      </Flex>
    );
  };

  return (
    <>
      <Flex vertical gap={12}>
        <Segmented<FollowUpView>
          value={view}
          onChange={setView}
          size="large"
          block
          options={[
            { value: 'mine', label: t('serviceDesk.followUps.views.mine') },
            { value: 'today', label: t('serviceDesk.followUps.views.today') },
            { value: 'overdue', label: t('serviceDesk.followUps.views.overdue') },
            { value: 'everyone', label: t('serviceDesk.followUps.views.everyone') },
          ]}
        />
        {error !== null && (
          <Text type="danger">{error instanceof ApiError ? error.message : t('serviceDesk.followUps.loadFailed')}</Text>
        )}
        <ListPage<FollowUp>
          title={t('serviceDesk.followUps.title')}
          loading={isFetching}
          emptyDescription={emptyFor[view]}
          actions={
            <Button
              icon={<DownloadOutlined />}
              onClick={() =>
                void downloadCsv(t, () => exportFollowUpsCsv({ view }), `follow-ups-${view}.csv`, 'serviceDesk.followUps.exportFailed')
              }
            >
              {t('serviceDesk.followUps.exportCsv')}
            </Button>
          }
          columns={[
            {
              title: t('serviceDesk.followUps.col.due'),
              key: 'due',
              width: 190,
              render: (_: unknown, r) => (
                <Flex gap={4} wrap align="center">
                  <Text type={r.overdue ? 'danger' : undefined}>{formatDateTime(r.dueAt)}</Text>
                  {overdueTag(r)}
                </Flex>
              ),
            },
            { title: t('serviceDesk.followUps.col.customer'), key: 'customer', render: (_: unknown, r) => r.customer.name },
            { title: t('serviceDesk.field.phone'), key: 'phone', width: 170, render: (_: unknown, r) => <PhoneLink phone={r.customer.phone} /> },
            {
              title: t('serviceDesk.followUps.col.call'),
              key: 'type',
              render: (_: unknown, r) => t(`serviceDesk.followUpType.${r.type}`, { defaultValue: r.type }),
            },
            { title: t('serviceDesk.followUps.col.bike'), key: 'bike', width: 130, render: (_: unknown, r) => bike(r) },
            { title: t('serviceDesk.followUps.col.owner'), key: 'owner', width: 130, render: (_: unknown, r) => r.owner.name },
            { title: '', key: 'action', width: 170, render: (_: unknown, r) => record(r) },
          ]}
          rows={rows}
          rowKey={(row) => row.uid}
          onRowClick={setSelected}
          hasMore={hasNextPage}
          onLoadMore={() => void fetchNextPage()}
          renderMobileRow={(row) => (
            <Flex vertical gap={6}>
              <Flex justify="space-between" align="center" gap={8}>
                <Text strong>{row.customer.name}</Text>
                <Flex gap={4}>{overdueTag(row)}</Flex>
              </Flex>
              <Text>{t(`serviceDesk.followUpType.${row.type}`, { defaultValue: row.type })}</Text>
              <PhoneLink phone={row.customer.phone} />
              <Text type={row.overdue ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                {formatDateTime(row.dueAt)} · {bike(row)}
              </Text>
              {record(row)}
            </Flex>
          )}
        />
      </Flex>

      {sending !== null && (
        <SendReminderDrawer
          key={sending.uid}
          open
          reminder={sending}
          onClose={() => { setSending(null); }}
          onDone={() => {
            setSending(null);
            void queryClient.invalidateQueries({ queryKey: ['follow-ups'] });
            void queryClient.invalidateQueries({ queryKey: ['reminders'] });
          }}
        />
      )}

      {selected !== null && (
        <FollowUpOutcomeDrawer
          key={selected.uid}
          open
          followUp={selected}
          onClose={() => { setSelected(null); }}
          onDone={() => {
            setSelected(null);
            void queryClient.invalidateQueries({ queryKey: ['follow-ups'] });
            void message.success(t('serviceDesk.drawer.saved'));
          }}
        />
      )}
    </>
  );
}
